/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import React, { useState } from 'react';
import { Button, Checkbox, Modal } from 'antd';
import { useStore } from '../stores/common';
import * as Selector from '../stores/selector';
import { useTranslation } from 'react-i18next';

interface LegalAcceptancePromptProps {
  onAccept: () => void;
  onDecline: () => void;
  submitting?: boolean;
}

const BASE = import.meta.env.BASE_URL || '/';
const TERMS_URL = BASE + 'terms';
const PRIVACY_URL = BASE + 'privacy';

// Acceptance modal: title, summary, links to the standalone Terms / Privacy
// pages, an "I agree" checkbox, and Accept / Decline buttons. Persistence
// (Firestore writes, stashing in pending state) is the caller's
// responsibility.
const LegalAcceptancePrompt = React.memo(({ onAccept, onDecline, submitting }: LegalAcceptancePromptProps) => {
  const language = useStore(Selector.language);
  const { t } = useTranslation();
  const lang = { lng: language };

  const [checked, setChecked] = useState(false);

  const linkStyle: React.CSSProperties = {
    color: '#1677ff',
    textDecoration: 'underline',
  };

  return (
    <Modal
      open
      title={t('legal.WelcomeToAIMS', lang)}
      // Closing via the X / Escape / scrim click is treated as a decline
      // so we never silently let the user past the gate.
      onCancel={onDecline}
      maskClosable={false}
      keyboard={false}
      width={440}
      zIndex={1003}
      footer={[
        <Button key="decline" onClick={onDecline} disabled={submitting}>
          {t('legal.Decline', lang)}
        </Button>,
        <Button key="accept" type="primary" disabled={!checked || submitting} loading={submitting} onClick={onAccept}>
          {t('legal.Accept', lang)}
        </Button>,
      ]}
    >
      <p style={{ margin: '0 0 14px', fontSize: '13.5px', lineHeight: 1.6 }}>
        {t('legal.AcceptancePromptPrefix', lang)}{' '}
        <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" style={linkStyle}>
          {t('aboutUs.TermsOfService', lang)}
        </a>{' '}
        {t('cookie.AgreementConnector', lang)}{' '}
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={linkStyle}>
          {t('aboutUs.PrivacyPolicy', lang)}
        </a>
        {t('legal.AcceptancePromptSuffix', lang)}
      </p>
      <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)}>
        {t('legal.IAgreeToTheTermsAndPrivacy', lang)}
      </Checkbox>
    </Modal>
  );
});

export default LegalAcceptancePrompt;
